import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { m } from 'motion/react';
import { AlertTriangle, CheckCircle2, CircleDashed, CircleSlash, Info, Wrench } from 'lucide-react';

import { useCompanyId } from './useCompany';
import { useAuth } from '../../stores/auth';
import { companies } from '../../api/endpoints';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { Card, PageHeader, Section } from '../../components/Surface';
import { RISE, STAGGER } from '../../components/motion';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { Counter } from '../../components/Counter';
import { useT } from '../../i18n/provider';

/**
 * El panel de empresa de `13 §3.1`.
 *
 * Los contadores nacieron a cero en la fase 1 y la forma no cambió después —la
 * decisión está registrada con el propietario—; hoy los cuenta el backend
 * contra las tablas que llenaron las fases 2 a 9, así que un cero en esta
 * pantalla significa cero. La pantalla sigue empezando por el bloque de salud:
 * una empresa sin credenciales no puede ejecutar nada, y esta es la pantalla
 * donde eso tiene que ser evidente en el primer minuto.
 */
export function CompanyPanel() {
  const t = useT();
  const id = useCompanyId();
  const { companySlug } = useParams({ from: '/authenticated/c/$companySlug' });
  const company = useAuth((state) => state.user?.companies.find((item) => item.slug === companySlug));
  const dashboard = useQuery({ queryKey: ['dashboard', id], queryFn: () => companies.dashboard(id), enabled: id !== '' });
  const health = useQuery({ queryKey: ['health', id], queryFn: () => companies.health(id), enabled: id !== '' });

  if (dashboard.isPending || health.isPending) {
    return <SkeletonRows rows={3} />;
  }

  if (dashboard.error) {
    return <ErrorBanner error={dashboard.error} onRetry={() => void dashboard.refetch()} />;
  }

  const alerts = (dashboard.data?.alerts ?? []) as { key: string; severity: string; message: string }[];

  // El ayudante solo aparece cuando queda algo que un plan pueda arreglar. Con
  // el panel entero en verde, o con lo único roto siendo una credencial, un
  // botón «proponer arreglos» invita a gastar una conversación en nada.
  const repairable = (health.data?.checks ?? []).some(
    (check) => (check.status === 'error' || check.status === 'warning') && !check.key.startsWith('credentials'),
  );
  const repairs = repairable ? (
    <Link to="/c/$companySlug/repairs" params={{ companySlug }}>
      <Button variant="secondary" size="sm" icon={<Wrench />}>
        {t('panel.openRepairs')}
      </Button>
    </Link>
  ) : null;

  const counts = [
    [t('panel.counts.employees'), 'employees', 'total'],
    [t('panel.counts.projects'), 'projects', 'total'],
    [t('panel.counts.activeTasks'), 'tasks', 'active'],
    [t('panel.counts.awaitingApproval'), 'tasks', 'pending_approval'],
  ] as const;

  // `13 §7`: el estado nunca va solo en el color. Cada comprobación lleva su
  // glifo y su palabra además del tono.
  const CHECK = {
    ok: { icon: CheckCircle2, tone: 'success' as const },
    warning: { icon: AlertTriangle, tone: 'warning' as const },
    error: { icon: CircleSlash, tone: 'danger' as const },
    not_applicable: { icon: CircleDashed, tone: 'neutral' as const },
  };

  return (
    <div className="space-y-9">
      {/*
        El nombre de la empresa vive aquí, en la pantalla, y no en el marco.

        Lo pintaba `CompanyLayout` encima de la fila de pestañas, así que las
        veintiuna secciones de la empresa compartían un mismo título y ninguna
        tenía el suyo. Ahora la barra dice en qué empresa estás y cada pantalla
        dice qué es; al panel le tocaba este, que es además el `<h1>` que la
        pantalla necesitaba para no quedarse sin encabezado.
      */}
      <PageHeader
        eyebrow={company?.slug}
        title={company?.name ?? companySlug}
        description={t('panel.intro')}
      />

      {alerts.length > 0 ? (
        <m.section variants={STAGGER} initial="hidden" animate="shown" className="space-y-2">
          {alerts.map((alert) => (
            <m.div
              key={alert.key}
              variants={RISE}
              role="status"
              className={`flex flex-wrap items-center gap-3 rounded-md border px-4 py-3 text-sm ${
                alert.severity === 'error' ? 'border-danger-line bg-danger-pale' : 'border-warning-line bg-warning-pale'
              }`}
            >
              {alert.severity === 'error' ? (
                <AlertTriangle className="size-4 shrink-0 text-danger" aria-hidden="true" />
              ) : (
                <Info className="size-4 shrink-0 text-warning" aria-hidden="true" />
              )}
              <span className="grow text-ink">{alert.message}</span>
              {alert.key.startsWith('credentials') ? (
                <Link
                  to="/c/$companySlug/settings/credentials"
                  params={{ companySlug }}
                  className="font-semibold whitespace-nowrap text-primary hover:underline"
                >
                  {t('panel.addCredential')}
                </Link>
              ) : null}
            </m.div>
          ))}
        </m.section>
      ) : null}

      <Section title={t('panel.thisMonth')}>
        <m.dl
          variants={STAGGER}
          initial="hidden"
          animate="shown"
          className="grid grid-cols-2 gap-3 sm:grid-cols-4"
        >
          {counts.map(([label, group, key]) => (
            <m.div key={label} variants={RISE}>
              <Card size="md" className="relative overflow-hidden">
                {/* El resplandor de marca, muy tenue, da profundidad a la fila
                    de contadores sin competir con la cifra. */}
                <span
                  aria-hidden="true"
                  className="brand-gradient pointer-events-none absolute -top-10 -right-10 size-24 rounded-full opacity-[0.07] blur-2xl"
                />
                <dt className="text-xs font-semibold text-ink-soft">{label}</dt>
                <dd className="mt-1.5 text-3xl font-semibold tracking-tight text-ink tabular-nums">
                  <Counter value={(dashboard.data?.[group] as Record<string, number> | undefined)?.[key] ?? 0} />
                </dd>
              </Card>
            </m.div>
          ))}
        </m.dl>
      </Section>

      <Section title={t('panel.readiness')} actions={repairs}>
        <ul className="divide-y divide-line overflow-hidden rounded-lg border border-line bg-raised">
          {health.data?.checks.map((check, index) => {
            const { icon: Icon, tone } = CHECK[check.status as keyof typeof CHECK] ?? CHECK.not_applicable;

            return (
              <m.li
                key={check.key}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.035, duration: 0.26, ease: [0.22, 0.8, 0.28, 1] }}
                className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm transition-colors hover:bg-hover"
              >
                <Icon
                  className={`size-4 shrink-0 ${
                    { success: 'text-success', warning: 'text-warning', danger: 'text-danger', neutral: 'text-ink-faint' }[
                      tone
                    ]
                  }`}
                  aria-hidden="true"
                />
                <span className="font-mono text-xs text-ink-faint">{check.key}</span>
                <span className="grow text-ink">{check.detail}</span>
                <Badge tone={tone} size="sm">
                  {t(`status.check.${check.status}` as 'status.check.ok')}
                </Badge>
                {/*
                  La fila es donde se lee el problema, así que es donde va el
                  botón —el mismo trato que en el organigrama—. Las dos filas de
                  credenciales no lo llevan y llevan el enlace de arriba: lo que
                  les falta es un secreto que pega una persona, y un ayudante
                  que se pusiera a proponer ahí propondría lo que no puede
                  cumplir.
                */}
                {check.status === 'ok' || check.status === 'not_applicable' || check.key.startsWith('credentials') ? null : (
                  <Link
                    to="/c/$companySlug/repairs"
                    params={{ companySlug }}
                    search={{ check: check.key }}
                    aria-label={t('panel.fixThisLabel', { check: check.key })}
                  >
                    <Button variant="ghost" size="sm">
                      {t('panel.fixThis')}
                    </Button>
                  </Link>
                )}
              </m.li>
            );
          })}
        </ul>
      </Section>
    </div>
  );
}
